import * as XLSX from 'xlsx';

interface DataRow {
    leaseName: string;
    coordinates: string;
    estGravity: string;
    bpd: string;
    bestMarket?: string;
    secBestMarket?: string;
}

interface AnalysisResult {
    leaseName: string;
    coordinates: string;
    trucksPerMonth: number;
    error: boolean;
    
    // All ranked options (up to 5)
    allOptions: Array<{
        closestMarket: string;
        distance: number;
        duration: string;
        displayName: string;
        truckingRate: number | null;
        marketDiscount: number | null;
        netValue: number | null;
        margin: number | null;
    }>;
}

export const downloadAnalysisAsExcel = (
    analysisResults: AnalysisResult[], 
    rows: DataRow[], 
    purchaseDeduct: number
): void => {
    // Create workbook
    const wb = XLSX.utils.book_new();

    // Standard column widths for all sheets
    const colWidths = [
        { wch: 20 }, // Lease Name
        { wch: 15 }, // Coordinates
        { wch: 12 }, // Est Gravity
        { wch: 8 },  // BPD
        { wch: 25 }, // Market
        { wch: 12 }, // Distance
        { wch: 12 }, // Rate
        { wch: 12 }, // Discount
        { wch: 12 }, // Net Value
        { wch: 12 }, // Purchase Deduct
        { wch: 12 }, // Margin
        { wch: 12 }, // Loads/Month
        { wch: 15 }, // Total/Month
    ];

    // Helper function to create data for a specific option rank
    const createSheetData = (optionIndex: number) => {
        return analysisResults
            .filter(result => !result.error && result.allOptions.length > optionIndex)
            .map(result => {
                const correspondingRow = rows.find(row => 
                    row.leaseName === result.leaseName
                );
                
                const option = result.allOptions[optionIndex];

                return {
                    'Lease Name': result.leaseName,
                    'Coordinates': correspondingRow?.coordinates || '',
                    'Est Gravity': correspondingRow?.estGravity || '',
                    'BPD': correspondingRow?.bpd || '',
                    'Market': option.displayName || '',
                    'Distance (mi)': option.distance || '',
                    'Rate ($)': option.truckingRate || '',
                    'Discount ($)': option.marketDiscount || '',
                    'Net Value ($)': option.netValue || '',
                    'Purchase Deduct ($)': purchaseDeduct || '',
                    'Margin ($)': option.margin || '',
                    'Loads/Month': result.trucksPerMonth || '',
                    'Total/Month ($)': result.trucksPerMonth && option.margin ? 
                        (result.trucksPerMonth * option.margin * 170) : '',
                };
            });
    };

    // Create sheets for each ranking (1st through 5th best)
    const rankNames = ['1st Best', '2nd Best', '3rd Best', '4th Best', '5th Best'];
    
    rankNames.forEach((rankName, index) => {
        const sheetData = createSheetData(index);
        
        // Only create sheet if there's data for this rank
        if (sheetData.length > 0) {
            const ws = XLSX.utils.json_to_sheet(sheetData);
            ws['!cols'] = colWidths;
            XLSX.utils.book_append_sheet(wb, ws, `${rankName} Results`);
        }
    });

    // Create a comprehensive "All Options" sheet
    const allOptionsData: Array<{
        'Lease Name': string;
        'Rank': number;
        'Coordinates': string;
        'Est Gravity': string;
        'BPD': string;
        'Market': string;
        'Distance (mi)': number | string;
        'Rate ($)': number | string;
        'Discount ($)': number | string;
        'Net Value ($)': number | string;
        'Purchase Deduct ($)': number | string;
        'Margin ($)': number | string;
        'Loads/Month': number | string;
        'Total/Month ($)': number | string;
    }> = [];
    
    analysisResults.forEach(result => {
        if (!result.error) {
            const correspondingRow = rows.find(row => 
                row.leaseName === result.leaseName
            );
            
            result.allOptions.forEach((option, index) => {
                allOptionsData.push({
                    'Lease Name': result.leaseName,
                    'Rank': index + 1,
                    'Coordinates': correspondingRow?.coordinates || '',
                    'Est Gravity': correspondingRow?.estGravity || '',
                    'BPD': correspondingRow?.bpd || '',
                    'Market': option.displayName || '',
                    'Distance (mi)': option.distance || '',
                    'Rate ($)': option.truckingRate || '',
                    'Discount ($)': option.marketDiscount || '',
                    'Net Value ($)': option.netValue || '',
                    'Purchase Deduct ($)': purchaseDeduct || '',
                    'Margin ($)': option.margin || '',
                    'Loads/Month': result.trucksPerMonth || '',
                    'Total/Month ($)': result.trucksPerMonth && option.margin ? 
                        (result.trucksPerMonth * option.margin * 170) : '',
                });
            });
        }
    });

    // Add "All Options" sheet with slightly wider rank column
    if (allOptionsData.length > 0) {
        const allOptionsWs = XLSX.utils.json_to_sheet(allOptionsData);
        const allOptionsColWidths = [
            { wch: 20 }, // Lease Name
            { wch: 6 },  // Rank
            { wch: 15 }, // Coordinates
            { wch: 12 }, // Est Gravity
            { wch: 8 },  // BPD
            { wch: 25 }, // Market
            { wch: 12 }, // Distance
            { wch: 12 }, // Rate
            { wch: 12 }, // Discount
            { wch: 12 }, // Net Value
            { wch: 12 }, // Purchase Deduct
            { wch: 12 }, // Margin
            { wch: 12 }, // Loads/Month
            { wch: 15 }, // Total/Month
        ];
        allOptionsWs['!cols'] = allOptionsColWidths;
        XLSX.utils.book_append_sheet(wb, allOptionsWs, 'All Options');
    }

    // Generate filename with timestamp
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const filename = `route_analysis_${timestamp}.xlsx`;

    // Download the file
    XLSX.writeFile(wb, filename);
};